import { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import {
  ChevronRight,
  Trophy,
  Target,
  GraduationCap,
  Clock,
  CheckCircle2,
  XCircle,
  Lightbulb
} from "lucide-react";
import { motion } from "framer-motion";
import elabAPI from "../../libs/api/elab";

export const getTestId = () => {
  const id = window.location.pathname.split("/")[3];
  return id;
};
export default function NewTestResults({setSelectedLevel}) {
  const [loading, setLoading] = useState(true);
  const [resultData, setResultData] = useState(null);

  // First useEffect for data fetching
  useEffect(() => {
    const fetchTestResults = async () => {
      try {
        const response = await elabAPI.get_result({ id: getTestId() });
        const data = await response.json();
        setResultData(data);
      } catch (error) {
        console.error("Error fetching test results:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestResults();
  }, []);

  useEffect(() => {
    if (resultData && setSelectedLevel) {
      const { student_info } = resultData;
      const level = Array.isArray(student_info.level[0])
        ? student_info.level[0][0]
        : student_info.level[0];
      
      if (level) {
        setSelectedLevel(level);
      }
    }
  }, [resultData, setSelectedLevel]);

  if (loading) {
    return (
      <div className={`centered text-light fs-4`}>
        <div class="spinner-border h-w-100" role="status"></div>
      </div>
    );
  }

  if (!resultData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        No data available
      </div>
    );
  }

  const { student_info, scores, categories } = resultData;
  // const getStatusByScore = (score, total) => {
  //   const percentage = (score / total) * 100
  //   if (percentage >= 80) return "distinction"
  //   if (percentage >= 60) return "pass"
  //   return "insufficient"
  // }

  const getLabelandColor = (score_grid) => {
    return [score_grid?.["status"] || "Insufficient", score_grid?.["color"]];
  };
  const getStatusColor = (color) => {
    return {
      backgroundColor: color || "grey",
    };
  };

  const getStatusBadge = (color) => {
    return {
      backgroundColor: "white",
      color: color || "grey",
    };
  };
  const levelColors = {
    B1: "[#9333ea]",
    B2: "[#EA580C]",
    C1: "[#db2777]",
  };

  const level = Array.isArray(student_info.level[0])
    ? student_info.level[0][0]
    : student_info.level[0];
    
  const getLevelColor = () => {
    const levelColorclass = levelColors[level];
    return levelColorclass;
  };
  // if (level){
  //   document.getElementById('set_bgcolor').classList.add(`bg-${levelColors[level]}`);
  //   document.querySelectorAll('.set_text_color').forEach(element => {
  //     element.classList.remove('text-dark');
  //     element.classList.add('text-white');
  //   });
  // }

  return (
    <div className="min-h-screen bg-white">
      <div className={`bg-${getLevelColor()} border-b pb-[115px] pt-24`}>
        <div className="mx-auto max-w-7xl px-4 pb-6 pt-2 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white">
                <Target className={`h-6 w-6 text-${getLevelColor()}`} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">
                  {student_info.student_name}
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-white/80">
                  <span className="flex items-center gap-1.5">
                    <Target className="h-4 w-4" />
                    {student_info.title}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <GraduationCap className="h-4 w-4" />
                    Level {student_info.level[0]}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-4 w-4 clock" />
                    {student_info.duration}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
              <div className="text-right">
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-white">
                    {scores.user_score}/{scores.total_score}
                  </span>
                  <div className="rounded-full bg-green-100 p-2">
                    <Trophy className="h-5 w-5 text-green-600" />
                  </div>
                </div>
                <p className="text-sm text-white/80">
                  Overall Score 
                  {/* ({scores.percentage.toFixed(2)}%) */}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="container mx-auto px-4 max-w-7xl -mt-[110px]">
        <Card className="bg-white border-0 overflow-hidden shadow-sm transition-shadow hover:shadow-md flex flex-col">
          <div className="w-100 py-8 sm:px-6 lg:px-10">
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900">
                {student_info.skill} Test Results
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Review your {student_info.skill.toLowerCase()} performance
                across all sections. Click on any section to review your
                answers.
              </p>
            </div>
            <div className="space-y-8">
                <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
                    {categories.map((category, index) => (
                        <>
                            {category.sections.map((section) => {
                                const [label, color] = getLabelandColor(section.score_grid);
                                return (
                                    <motion.div
                                    key={category.name}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: index * 0.1 }}
                                >
                                <Card
                                    key={section.section_id}
                                    className="overflow-hidden shadow-sm transition-shadow hover:shadow-md"
                                >
                                    <CardContent className="p-0 flex-grow">
                                    <div className={`p-4`} style={getStatusColor(color)}>
                                        <div className="flex items-center justify-between">
                                        <h4 className="text-lg font-semibold text-white">
                                            Section {section.section_number}
                                        </h4>
                                        <div
                                            className={`rounded-full px-2 py-1 text-xs font-medium`}
                                            style={getStatusBadge(color)}
                                        >
                                            {label.charAt(0).toUpperCase() + label.slice(1)}
                                        </div>
                                        </div>
                                        <p className="mt-1 text-base font-medium text-white">
                                        {section.section_name}
                                        </p>
                                    </div>
                                    <div className="space-y-4 p-4 flex-grow flex flex-col">
                                        <div className="mb-4 w-100">
                                        <div className="mb-2 text-sm font-medium">
                                            Score
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <div className="text-3xl font-bold">
                                            {section.score}/{section.total}
                                            </div>
                                            <div className="flex items-center gap-4 text-sm">
                                            <div className="flex items-center gap-1">
                                                <CheckCircle2 className="h-5 w-5 text-green-500" />
                                                <span>{section.correctAnswers}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <XCircle className="h-5 w-5 text-red-500" />
                                                <span>{section.incorrectAnswers}</span>
                                            </div>
                                            </div>
                                        </div>
                                        <Progress
                                            value={(section.score / section.total) * 100}
                                            className="mt-2 h-2"
                                        />
                                        </div>
                                        {section.insight && (
                                        <div className="rounded-lg bg-amber-50 p-3 flex-grow">
                                            <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                                            <Lightbulb className="h-4 w-4 text-amber-500" />
                                            Insight
                                            </div>
                                            <p className="mt-1 text-sm text-gray-900">
                                            {section.insight}
                                            </p>
                                        </div>
                                        )}
                                        <Button
                                        className="w-full mt-auto"
                                        variant="outline"
                                        onClick={() => {
                                            window.location.href = `/elab/quiz/${getTestId()}?type=assessment&section_id=${
                                            section.section_id
                                            }`;
                                        }}
                                        >
                                        Review Section
                                        <ChevronRight className="ml-2 h-4 w-4" />
                                        </Button>
                                    </div>
                                    </CardContent>
                                </Card>
                                </motion.div>
                                );
                            })}
                        </>
                    ))}
                </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
